import { PRINTOS_SELECTOR } from '@/mount-helper';
import get from 'lodash.get';

import {useSessionStore} from '@/stores/Session.store';
import { useL10NStore} from '@/stores/i18n.store';
import { initI18n } from '@/i18n'

import {READY_GATE} from '@/services/bootstrap';
import services from '@/services/index';

// One hour, exported for testing
export const MINIMAL_SESSION_TIMEOUT_MILLIS = 60 * 60 * 1000;

export default function sessionServiceFactory(services) {
  let thisService = {
    bootstrapSessionAsync,
    initSessionAsync,
    isAnonymousAllowed,
    isHotJarEnabled,
    isDeviceAllowed,
    isHeadless,
    isSuppress,
    isAuthenticated,
    checkTermsOfService,
    goApp,
    goHome,
    goSignIn,
    goSignOut,
    getUserData,
    isHPRoaming,
    getUserId,
    getPermissionsAsync,
    getPermissions,
    getServicePermissions,
    hasPermission,
    getContexts,
    getContextOptions,
    getContextById,
    getContext,
    getContextId,
    getContextName,
    getContextType,
    setContextAsync,
    openContext,
    getClientConfig,
    getClientConfigAsync,
    getClientConfigLegacy,
    getEntitlementsAsync,
    getEntitlementFeature,
    getEntitlementApplication,
    getSitesAsync,
    getSites,
    addSiteAsync,
    updateSiteAsync,
    deleteSiteAsync,
    getSiteOptions,
    getOrg,
    getOrgLinks,
    getOrgId: getContextId,
    getOrgType: getContextType,
    updateOrgAsync,
    getContextsAsync,
    getEntityType,
    getManagingContext,
    getValidators,
    getValidator,
    getBusinessUnits,
    getUser,
    getUserJobTitle,
    getUserJobFunctions,
    getUserJobFunctionOptions,
    getRegistryKeys,
    getRegistryServices,
    getRegistryValue,
    getRegistryService,
    getFirstName,
    getLastName,
    getFullName,
    getInitials,
    getIdentityProvider,
    hasFeature,
    addFeature,
    removeFeature,
    getFeatures,
    hasPersona,
    hasEntity,
    is3D,
    isIndigo,
    isLF,
    isScitex,
    isPageWide,
    is3DOnly,
    isIndigoOnly,
    isLFOnly,
    isScitexOnly,
    isPageWideOnly,
    isHP,
    isHPInternal,
    isPSP,
    isChannel,
    isBrand,
    isOrphan,
    isOneHP,
    isHPOperation,
    isGeneric,
    isExternal,
    isDevice,
    isUser,
    getRoles,
    getUserPermissions,
    getAlternateAppName,
    setAlternateAppName,
    overrideHomeBreadcrumb,
    isHomeBreadcrumbOverride,
    getHomeBreadcrumbOverride,
    getTheme,
    setTheme,
    isDarkTheme,
    isLightTheme,
    updateTheme,
    hide,
    show,
    sendClientEvent,
    HTTPGet,
    HTTPPost,
    HTTPPut,
    HTTPPatch,
    HTTPDelete,
    HTTPFetch,
    HTTPHead,
    setDebug,
    getDebug,
    isDebug,
    log,
  };

  let i18n;
  let sessionStore;
  let anchorContext = null;

  return thisService;

  ////////////////////
  async function bootstrapSessionAsync() {
    sessionStore = useSessionStore();
    services.items.init();
    services.items.setOptions({
      id: 'contexts',
      idPath: 'id',
      items: {
        service: 'aaa',
        api: 'users/context',
        config: {version:4},
        dataPath: 'data.contexts',
        totalPath: 'data.total',
        type: 'paged',
        limit: 10000,
      },
    });
    return getClientConfigAsync(false).then(cc => {
      anchorContext = cc.context?.id;
      services.app.init();
      sessionStore.setTargetUrl(services.window.location.href);
      let hasValidToken = !!cc._links?.signOut || cc.entity?.type === "Device";
      let allowAnonymous = isAnonymousAllowed();
      sessionStore.setIsAuthenticated(hasValidToken);
      if (!hasValidToken && !allowAnonymous) {
        console.warn('PrintOS Header: SignIn Redirect - Application does not allow anonymous access', services.app.getCurrentAppId());
        goSignIn(true);
        return false;
      }

      return services.preferences.initAsync().then(() => {
        return initI18n().then(() => {
          return services.i18n.initAsync().then(() => {
            i18n = services.i18n.$t;
            window.onfocus = restoreContext;
            return true;
          });
        });
      });
    })
      .catch(e => {
        // Could not get client config
        console.warn('PrintOS Header: SignIn Redirect - Could not get initial client config', e);
        if (services.app.getCurrentAppId() !== 'register') {
          goSignOut(true);
        }
        return Promise.resolve(false);
      })
      .finally(() => {

      });
  }

  function restoreContext() {
    console.log('Checking Context', anchorContext);
    if (!document.hidden && isUser()) {
      if (isUser()) {
        validateUserTokenAsync(false).then(resp => {
          let tabContext = getContextName(anchorContext);
          if (resp.context.id !== anchorContext) {
            console.log(`Restoring Context from ${resp.context.id} to ${anchorContext}`);
            services.ui.showProgress();
            window.$printos.client.v1.sendClientEvent('posh-progress-update', {message: i18n('header.ui.LoadingContext', [tabContext])});
            return setContextAsync(anchorContext, false);
          }
          else {
            console.log(`Already in context ${anchorContext}`);
          }
        })
          .catch(() => {
            console.warn('PrintOS Header: Home Redirect - Session no longer valid');
            return goSignOut(true);
          })
          .finally(() => {
            services.ui.hideProgress();
          });
      }
    }
  }

  async function initSessionAsync() {
    services.db.init();
    services.device.init();
    services.date.init();
    services.go.init();
    services.ui.init();

    let cc = sessionStore.cc;
    let currentAppId = services.app.getCurrentAppId();
    initSites();
    let promises = [];
    if (hasEntity() && isUser()) {
      if (isUser) {
        promises.push(validateUserTokenAsync());
        // promises.push(getContextsAsync(true));
        if (!isOrphan()) {
          promises.push(getOrgAsync());
        }
      }
      promises.push(services.device.getDevicesAsync());
      promises.push(getEntitlementsAsync());
    }
    return Promise.all(promises).finally(() => {
      // authenticated
      let authenticated = !!cc._links?.signOut;
      let allowAnonymous = isAnonymousAllowed();
      let isDevice = cc.entity?.type === 'Device';

      if (!currentAppId) {
        if (authenticated && cc.entity?.type === "User") {
          console.warn('PrintOS Header: Home Redirect - Could not determine appId (Authenticated User)');
          return goHome();
        }
        else {
          console.warn('PrintOS Header: SignIn Redirect - Could not determine appId (non-User)');
          return goSignOut();
        }
      }

      let allowedAppIds = services.app.getApps().map(a => { return a.appId });
      if (allowAnonymous) {
        allowedAppIds = [...new Set([...allowedAppIds, ...[currentAppId]])];
      }

      if (!allowedAppIds.includes(currentAppId)) {
        if (isDevice) {
          console.warn('PrintOS Header: PrintBeat Redirect - Application not allowed for device', currentAppId);
          return goApp('printbeat');
        }
        else {
          console.warn('PrintOS Header: Home Redirect - Application not allowed for user', currentAppId);
          return goHome();
        }
      }

      if (services.window.location.host.endsWith('.cn')) {
        services.window.document.title = '打印操作系统';
      }
      else {
        let currentApp = services.app.getCurrentApp();
        services.window.document.title = currentApp ? `PrintOS - ${currentApp.loc.name}` : 'PrintOS';
      }

      // We are doing this here because we need to know we are NOT in the Start application
      if (currentAppId !== 'register') {
        services.session.checkTermsOfService();
      }

      startSessionMonitor();
      startContextMonitor();
      installUnauthInterceptor();

      services.bootstrap.readyGateCleared(READY_GATE.session);
    });
  }

  function validateUserTokenAsync(store = true) {
    return services.http.get('aaa', `users/validate`, {version: 4}).then(resp => {
      if (store) {
        sessionStore.setUserData(resp.data);
        let cc = sessionStore.cc;
        cc.entity.user = resp.data.user;
        sessionStore.setClientConfig(cc);
      }
      return resp.data;
    })
      .catch(() => {
        console.log('PrintOS Header: Entity not authenticated', sessionStore.cc);
        return Promise.resolve();
      });
  }

  function isDeviceAllowed() {
    let printosElement = services.document.body.querySelector(PRINTOS_SELECTOR);
    if (!printosElement) {
      // fall back to gz-body-wrap as a concession to gazelle, which might have loaded yet
      printosElement = services.document.body.querySelector('gz-body-wrap');
      if (!printosElement) {
        return false;
      }
    }

    return printosElement.hasAttribute('allow-device');
  }

  function isHotJarEnabled() {
    let printosElement = services.document.body.querySelector(PRINTOS_SELECTOR);
    if (!printosElement) {
      // fall back to gz-body-wrap as a concession to gazelle, which might have loaded yet
      printosElement = services.document.body.querySelector('gz-body-wrap');
      if (!printosElement) {
        return false;
      }
    }

    return printosElement.hasAttribute('hotjar');
  }

  function isAnonymousAllowed() {
    let printosElement = services.document.body.querySelector(PRINTOS_SELECTOR);
    if (!printosElement) {
      // fall back to gz-body-wrap as a concession to gazelle, which might have loaded yet
      printosElement = services.document.body.querySelector('gz-body-wrap');
      if (!printosElement) {
        return false;
      }
    }

    return printosElement.hasAttribute('allow-anonymous');
  }

  function isHeadless() {
    let headerElement = services.window.document.querySelector('printos-header');
    // return headerElement ? headerElement.hasAttribute('headless') : false;
    // Hack: Print Beat OEE currently has a custom solution to hide the header that we cannot detect.
    // ---This works today because the application is marked as allow-anonymous
    // ---We also hide the header if the app-id is print beat, anonymous is allowed and we are NOT authenticated
    return headerElement ? headerElement.hasAttribute('headless') || (services.app.getCurrentAppId() === 'printbeat' && !isAuthenticated() && isAnonymousAllowed()): false;
  }

  function isSuppress() {
    let headerElement = services.window.document.querySelector('printos-header');
    return headerElement ? headerElement.hasAttribute('suppress') : false;
  }

  function getClientConfigLegacy(cache = true) {
    return getClientConfigAsync(!cache);
  }

  function getClientConfigAsync(refresh = true) {
    let cachedClientConfig = sessionStore?.cc;
    if (!refresh && cachedClientConfig) {
      return Promise.resolve(cachedClientConfig);
    }

    const config = {
      // Update the cache when not caching
      cache: refresh ? 'refresh' : true,
      version: 2,
    };
    return services.http.get('portal_service', 'client/config', config).then(resp => {
      let requestUrl = resp.request?.responseURL;
      if (requestUrl) {
        services.performance.resourceTimingTrack(requestUrl);
      }

      sessionStore.setClientConfig(JSON.parse(JSON.stringify(resp.data)));
      if (sessionStore.cc?.contexts) {
        services.items.setItems('contexts', sessionStore.cc.contexts, 'contexts');
        // sessionStore.setContexts(sessionStore.cc.contexts);
      }

      if (!sessionStore.cc.permissions) {
        sessionStore.cc.permissions = [];
      }
      sessionStore.setPermissions(sessionStore.cc.permissions);

      sendClientEvent('pros-cc-updated', {cc:sessionStore.cc});
      return sessionStore.cc;
    })
    .catch(e => {
      console.log('Session Service: Error getting client config', e);
      return Promise.resolve();
    });
  }

  function HTTPGet(service, path, config) {
    let myConfig = config ? config : {};
    return services.http.get(service, path, myConfig);
  }

  function HTTPPost(service, path, payload, config) {
    let myConfig = config ? config : {};
    let myPayload = hasValue(payload) ? payload : {};
    return services.http.post(service, path, myPayload, myConfig);
  }

  function HTTPPut(service, path, payload, config) {
    let myConfig = config ? config : {};
    let myPayload = hasValue(payload) ? payload : {};
    return services.http.put(service, path, myPayload, myConfig);
  }

  function HTTPPatch(service, path, payload, config) {
    let myConfig = config ? config : {};
    let myPayload = hasValue(payload) ? payload : {};
    return services.http.patch(service, path, myPayload, myConfig);
  }

  function HTTPDelete(service, path, config) {
    let myConfig = config ? config : {};
    return services.http.delete(service, path, myConfig);
  }

  function HTTPFetch(url, config) {
    let myConfig = config ? config : {};
    return services.http.fetch(url, myConfig);
  }

  function HTTPHead(service, path, config) {
    let myConfig = config ? config : {};
    return services.http.head(service, path, myConfig);
  }

  function getClientConfig() {
    if (!sessionStore.cc) {
      console.warn('Missing Client Config');
      return null;
    }

    return sessionStore.cc;
  }

  function getValidators() {
    return sessionStore.cc.validators.map(v => {
      return {
        id: v.validationId,
        expression: v.validation,
        flags: v.validationFlags,
      };
    });
  }

  function getValidator(id) {
    return getValidators().find(v => {
      return v.id === id;
    });
  }

  function getEntityType() {
    return get(sessionStore.cc, 'entity.type');
  }

  function getUserId() {
    return get(sessionStore.cc, 'entity.id');
  }

  function getManagingContext() {
    return get(sessionStore.cc, 'managingContext');
  }

  function getBusinessUnits() {
    return get(sessionStore.cc, 'context.bu', []);
  }

  function hasFeature(featureId) {
    return get(sessionStore.cc, 'features', []).includes(featureId);
  }

  function addFeature(featureId) {
    if (!isHPOperation() && !getManagingContext()?.name === 'HP Inc.') {
      console.warn('This API is only supported for HP Operation users');
      return;
    }

    let features = get(sessionStore.cc, 'features', []);
    if (!features.includes(featureId)) {
      features.push(featureId);
    }
  }

  function removeFeature(featureId) {
    if (!isHPOperation() && !getManagingContext()?.name === 'HP Inc.') {
      console.warn('This API is only supported for HP Operation users');
      return;
    }

    let features = get(sessionStore.cc, 'features', []);
    sessionStore.cc.features = features.filter(f => { f !== featureId });
  }

  function getFeatures() {
    return get(sessionStore.cc, 'features', []);
  }

  function isHP() {
    return get(sessionStore.cc, 'context.type') === 'HP';
  }

  function isHPInternal() {
    return get(sessionStore.cc, 'context.org.hpInternal');
  }

  function isPSP() {
    return get(sessionStore.cc, 'context.type') === 'PSP';
  }

  function isChannel() {
    return get(sessionStore.cc, 'context.type') === 'Channel';
  }

  function isBrand() {
    return get(sessionStore.cc, 'context.type') === 'Brand';
  }

  function isOrphan() {
    return get(sessionStore.cc, 'context.type', 'Unknown') === 'Unknown';
  }

  function isOneHP() {
    return getIdentityProvider() === 'onehp';
  }

  function isHPOperation() {
    let roles = sessionStore.cc?.entity?.roles || [];
    return !!roles.find(r => { return r.systemroleName === 'HP Operation' });
  }

  function isGeneric() {
    return get(sessionStore.cc, 'context.type') === 'Generic';
  }

  function is3D() {
    return getBusinessUnits().includes('ThreeD');
  }

  function is3DOnly() {
    return getBusinessUnits().length === 1 && getBusinessUnits().includes('ThreeD');
  }

  function isIndigo() {
    return getBusinessUnits().includes('Indigo');
  }

  function isIndigoOnly() {
    return getBusinessUnits().length === 1 && getBusinessUnits().includes('Indigo');
  }

  function isLF() {
    return getBusinessUnits().includes('LargeFormat');
  }

  function isLFOnly() {
    return getBusinessUnits().length === 1 && getBusinessUnits().includes('LargeFormat');
  }

  function isScitex() {
    return getBusinessUnits().includes('Scitex');
  }

  function isScitexOnly() {
    return getBusinessUnits().length === 1 && getBusinessUnits().includes('Scitex');
  }

  function isPageWide() {
    return getBusinessUnits().includes('PWI');
  }

  function isPageWideOnly() {
    return getBusinessUnits().length === 1 && getBusinessUnits().includes('PWI');
  }

  function getAlternateAppName() {
    return window.printos?.blue?.ui?.internal?.appName;
  }

  function setAlternateAppName(appName) {
    if (!appName && get(window, 'printos.blue.ui.internal.appName')) {
      delete window.printos.blue.ui.internal.appName;
    }
    else {
      services.helpers.set(window, 'printos.blue.ui.internal.appName', appName);
    }

    services.window.dispatchEvent(new CustomEvent('posb-app-name-changed', {detail: {appName: appName ? appName : undefined}}));
  }

  function overrideHomeBreadcrumb(value = null) {
    sessionStore.setHomeBreadcrumb(value);
  }

  function isHomeBreadcrumbOverride() {
    return sessionStore.homeBreadcrumb !== undefined;
  }

  function getHomeBreadcrumbOverride() {
    return sessionStore.homeBreadcrumb;
  }

  function setTheme(isDarkMode) {
    return services.preferences.setPreferenceAsync('darkMode', isDarkMode, 'top-bar').then(() => {
      return updateTheme(isDarkMode);
    });
  }

  function hide() {
    services.window.document.body.setAttribute('pos--headless', 'true');
  }

  function show() {
    services.window.document.body.setAttribute('pos--headless', 'false');
  }

  function getTheme() {
    const theme = services.window.document.body.getAttribute('pos--theme');
    return theme ? theme : 'dark';
  }

  function isDarkTheme() {
    return getTheme() === 'dark';
  }

  function isLightTheme() {
    return getTheme() === 'light';
  }

  function updateTheme(isDarkMode) {
    if (isDarkMode) {
      services.window.document.body.setAttribute('pos--theme', 'dark');
    }
    else {
      services.window.document.body.setAttribute('pos--theme', 'light');
    }

    services.window.dispatchEvent(new CustomEvent('posh-theme-changed', {detail: {theme: isDarkMode ? 'dark' : 'light'}}));
    return isDarkMode ? 'dark' : 'light';
  }

  function getUserPermissions(cache = true) {
    if (!isAuthenticated()) {
      return Promise.resolve();
    }

    const config = {
      // Update the cache when not caching
      cache: cache ? true : 'refresh',
      version: 2,
    };
    let path = 'requests/types';
    return services.http.get('supportapp_service', path, config)
      .then(response => {
        let requestUrl = get(response, 'request.responseURL');
        if (requestUrl) {
          services.performance.resourceTimingTrack(requestUrl);
        }

        return response.data;
      })
      .catch(e => {
        services.ga.exceptionTrack('Failed to retrieve RCDA user permissions: ' + JSON.stringify(e));
        return Promise.reject(e);
      });
  }

  function isHPRoaming() {
    if (!sessionStore.cc.managingContext) {
      return false;
    }

    return sessionStore.cc.managingContext.type === 'HP';
  }

  function isDevice() {
    return get(sessionStore.cc, 'entity.type') === 'Device';
  }

  function isUser() {
    return get(sessionStore.cc, 'entity.type') === 'User';
  }

  function isExternal() {
    if (isOrphan()) {
      return false;
    }

    if (sessionStore.cc.managingContext) {
      return false;
    }

    return getRoles.filter(r => {
      return !r.externalAccess;
    }).length === 0;
  }

  function getRoles() {
    return get(sessionStore.cc, 'entity.roles', []);
  }

  function getUserJobTitle() {
    return get(sessionStore.cc, 'entity.persona.jobTitle');
  }

  function getUserJobFunctions() {
    return get(sessionStore.cc, 'entity.persona.myTypes', []);
  }

  function getUserJobFunctionOptions() {
    let options = [];
    let personaTypes = get(sessionStore.cc, 'entity.persona.types', []);
    personaTypes.forEach(pt => {
      if (pt !== 'Other' && pt !== 'NoValue') {
        options.push({
          value: pt,
          display: i18n(`uxf.uxf.Persona_${pt}`),
        });
      }
    });

    options = options.sort((a,b) => {
      return a.display.trim().toLocaleLowerCase().localeCompare(b.display.trim().toLocaleLowerCase());
    });

    //Special case move Other and NoValue to end
    if (personaTypes.includes('Other')) {
      options.push({
        value: 'Other',
        display: i18n('uxf.uxf.Persona_Other'),
      });
    }

    return options;
  }

  function getRegistryKeys() {
    return get(sessionStore.cc, `registry.keys`);
  }

  function getRegistryServices() {
    return get(sessionStore.cc, `registry.services`);
  }

  function getRegistryValue(key) {
    return get(sessionStore.cc, `registry.keys.${key}`);
  }

  function getRegistryService(key) {
    return get(sessionStore.cc, `registry.services.${key}`);
  }

  function getUser() {
    return get(sessionStore.cc, 'entity');
  }
  function getFirstName() {
    return get(sessionStore.cc, 'entity.firstName');
  }

  function getLastName() {
    return get(sessionStore.cc, 'entity.lastName');
  }

  function getFullName() {
    return get(sessionStore.cc, 'entity.fullName');
  }

  function getInitials() {
    const firstInitial = sessionStore.cc.entity?.firstName ? sessionStore.cc.entity.firstName.charAt(0).toLocaleUpperCase() : '';
    const lastInitial = sessionStore.cc.entity?.lastName ? sessionStore.cc.entity.lastName.charAt(0).toLocaleUpperCase() : '';

    return firstInitial !== '' || lastInitial !== '' ? firstInitial + lastInitial : null;
  }

  function getIdentityProvider() {
    return get(sessionStore.cc, 'entity.identityProvider');
  }

  function hasPersona() {
    return get(sessionStore.cc, 'entity.persona.myTypes', []).length > 0;
  }

  function hasEntity() {
    return !!get(sessionStore.cc, 'entity');
  }

  function getPermissionsAsync() {
    return this.getClientConfigAsync(true).then(() => {
      return sessionStore.permissions;
    })
  }

  function getPermissions() {
    return sessionStore.permissions;
  }

  function getServicePermissions(serviceName) {
    return sessionStore.permissions[serviceName];
  }

  function hasPermission(serviceName, permission) {
    return sessionStore.permissions && sessionStore.permissions[serviceName] ? sessionStore.permissions[serviceName].includes(permission) : false;
  }

  function getContextsAsync(refresh = false) {
    return services.items.getItemsAsync('contexts', refresh, null, null);
  }

  function getContexts(type = null) {
    let contexts = services.items.getItems('contexts');
    // let contexts = sessionStore.contexts ? sessionStore.contexts : [];
    return type ? contexts.filter(c => { return c.type  === type }) : contexts;
  }

  function getContextOptions(type = null) {
    let contexts = services.items.getItems('contexts');
    // let contexts = sessionStore.contexts ? sessionStore.contexts : [];
    if (type) {
      contexts = contexts.filter(c => { return c.type === type });
    }

    let contextOptions = [];
    contexts.forEach(c => {
      contextOptions.push({
        display: c.name.trim() === '' ? '[Empty Name]': c.name.trim(),
        value: c,
      });
    });

    contextOptions = contextOptions.sort((a,b) => {
      return a.display.toLocaleLowerCase().localeCompare(b.display.toLocaleLowerCase());
    });

    return contextOptions;
  }

  function getContextById(id) {
    let contexts = services.items.getItems('contexts');
    // let contexts = sessionStore.contexts ? sessionStore.contexts : [];
    return contexts.find(c => { return c.id === id });
  }

  function getContext() {
    return get(sessionStore.cc, 'context');
  }

  function getContextId() {
    return get(sessionStore.cc, 'context.id');
  }

  function getContextName() {
    return get(sessionStore.cc, 'context.name');
  }

  function getContextType() {
    return get(sessionStore.cc, 'context.type');
  }

  function setContextAsync(orgId, reload = true) {
    let partialContext = {
      id: orgId,
    };

    let currentAppId = services.app.getCurrentAppId();
    return services.http.put('aaa', 'users/context', partialContext, { version: 4 }).then(() => {
      services.ga.eventTrack('account', 'org context switch', `${getContextId()}|${orgId}`, undefined, { sessionControl: 'end' });
      return getClientConfigAsync(true).then(() => {
        if (reload) {
          goApp(currentAppId);
        }
      })
    });
  }

  function openContext(orgId) {
    let context = services.items.getItems('contexts').find(c => { return c.id === orgId });
    if (!context) {
      return;
    }

    services.ui.removeInlineAlert(`open-context-${orgId}`);
    sessionStore.addOpenContext(orgId, window.open(`/start/#/signin?orgId=${orgId}`, orgId));
    services.ui.addInlineAlert({
      id: `open-context-${orgId}`,
      severity: 'info',
      title: services.i18n.$t('header.inlineAlerts.NonHPContext'),
      description: services.i18n.$t('header.inlineAlerts.NonHPContextDescription', [context.name]),
      cooldown: 0,
      hideClose: true,
      actions: [
        {
          type: 'link',
          title: services.i18n.$t('header.inlineAlerts.CloseContext'),
          callback: () => {
            closeContext(orgId);
          },
        }
      ],
    });
  }

  function closeContext(orgId) {
    let windowRef = sessionStore.getOpenContext(orgId);
    if (windowRef) {
      windowRef.close();
    }
    services.ui.removeInlineAlert(`open-context-${orgId}`);
    sessionStore.removeOpenContext(orgId);
  }

  function getOrgAsync() {
    if (sessionStore.org) {
      return Promise.resolve(sessionStore.org);
    }

    return services.http.get('aaa', 'organizations', {version:4}).then(resp => {
      sessionStore.setOrgResponse(resp);
      return resp;
    })
      .catch(() => {
        console.log('PrintOS Header: Unable to resolve organization', sessionStore.cc);
        return Promise.resolve();
      });
  }

  function getEntitlementsAsync(refresh = false) {
    if (!refresh && sessionStore.entitlements) {
      return Promise.resolve(sessionStore.entitlements);
    }
    return services.http.get('enforcer', 'organizations/entitlements').then(resp => {
      sessionStore.setEntitlements(resp.data);
      return resp.data;
    })
      .catch(() => {
        console.warn('PrintOS Header: Unable to resolve enforcer entitlements');
        return Promise.resolve();
      });
  }

  function getEntitlementFeature(featureId) {
    if (!sessionStore.entitlements?.features) {
      return null;
    }

    return sessionStore.entitlements.features.find(f => { return f.name === featureId });
  }

  function getEntitlementApplication(appId) {
    if (!sessionStore.entitlements?.applications) {
      return null;
    }

    return sessionStore.entitlements.applications.find(a => { return a.name === appId });
  }

  function initSites() {
    let cc = sessionStore.cc;

    if (cc.context?.sites) {
      cc.context.sites.forEach(s => {
        s.location = getFullAddress(s.primaryAddress);
      });
    }
    sessionStore.setClientConfig(cc);
  }

  function getFullAddress(address) {
    let location = '';

    if (address) {
      if (address.addressLine1) {
        location += address.addressLine1 + ' ';
      }
      if (address.addressLine2) {
        location += address.addressLine2 + ' ';
      }
      if (address.locality) {
        location += address.locality + ' ';
      }
      if (address.region) {
        location += address.region + ' ';
      }
      if (address.postalCode) {
        location += address.postalCode + ' ';
      }
      if (address.country) {
        location += services.i18n.getCountryDisplay(address.country);
      }
    }

    return location;
  }

  function getSites() {
    return sessionStore.cc?.context?.sites ? sessionStore.cc.context.sites : [];
  }

  function getSitesAsync() {
    return services.http.get('aaa', 'organizations/sites', {version:4, params:{limit:1000}}).then(resp => {
      let cc = sessionStore.cc;
      cc.context.sites = resp?.data?.sites ? resp.data.sites : [];
      cc.context.sites = cc.context.sites.sort((a,b) => {
        return a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());
      });
      initSites();
      return cc.context.sites;
    }).catch(() => {
      let cc = sessionStore.cc;
      cc.context.sites = [];
      return Promise.resolve(cc.context.sites);
    });
  }

  function addSiteAsync(data) {
    return services.http.post('aaa', 'organizations/sites', data, {version:4}).then(resp => {
      let site = resp.data;
      site.location = getFullAddress(site.primaryAddress);
      sessionStore.cc.context.sites.push(site);

      return site;
    });
  }

  function updateSiteAsync(siteId, data) {
    return services.http.put('aaa', `organizations/sites/${siteId}`, data, {version:4}).then(resp => {
      let site = resp.data;
      site.location = getFullAddress(site.primaryAddress);
      sessionStore.cc.context.sites = sessionStore.cc.context.sites.map(s => {
        return s.siteId === siteId ? site : s;
      });

      return site;
    });
  }

  function deleteSiteAsync(siteId) {
    return services.http.delete('aaa', `organizations/sites/${siteId}`, {version:4}).then(() => {
      sessionStore.cc.context.sites = sessionStore.cc.context.sites.filter(s => {
        return s.siteId !== siteId;
      });
    });
  }

  function getSiteOptions() {
    let sites = sessionStore.cc?.context?.sites ? sessionStore.cc.context.sites : [];
    sites = sites.sort((a,b) => {
      return a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());
    });

    return sites.map(s => {
      return {
        display: s.name,
        value: s.siteId
      }
    });
  }

  function getOrg() {
    return sessionStore.orgResponse?.data;
  }

  function updateOrgAsync(org) {
    return services.http.put('aaa', 'organizations', org, {version:4}).then(resp => {
      sessionStore.orgResponse.data = resp.data;
      return resp.data;
    });
  }

  function getOrgLinks() {
    const links = sessionStore.orgResponse?.data?._links ? sessionStore.orgResponse.data._links : {};
    return Object.keys(links);
  }

  function isAuthenticated() {
    return sessionStore.isAuthenticated;
  }

  function getUserData() {
    return sessionStore.userData;
  }

  // function ensureValidContext() {
  //   const contexts = services.items.getItems('contexts');
  //   if (get(sessionStore.cc, 'entity.type') === 'Device') {
  //     return true;
  //   }
  //
  //   let contextId = get(sessionStore.cc, 'context.id');
  //   if (!contextId) {
  //     return true;
  //   }
  //
  //   let validContext = contexts.find(ctx => {
  //     return ctx.id === contextId;
  //   });
  //
  //   if (!validContext) {
  //     console.log('PrintOS Header: SignIn Redirect - No valid context');
  //     services.window.location.href = get(sessionStore.cc, '_links.signOut');
  //     return false;
  //   }
  //
  //   return true;
  // }

  function checkTermsOfService() {
    if (!isAuthenticated()) {
      return;
    }

    let termsHref = get(sessionStore.cc, '_links.terms.href');
    if (termsHref) {
      // Ensure we're not already redirected to terms
      if (services.window.location.href.indexOf(termsHref) === -1) {
        console.log('PrintOS Header: TermsOfService Redirect - checkTermsOfService')
        services.window.location.href = appendTarget(termsHref);
      }
    }
  }

  function goApp(appId) {
    let app = services.app.getAppById(appId);
    if (app && app._links?.self) {
      console.log(`PrintOS Header: goApp Navigation - To App (${appId})`, app._links?.self);
      services.window.location.href = app._links?.self;
    }
    else {
      console.log('PrintOS Redirect: goApp Navigation - No self link for app');
      goHome(true);
    }
  }

  function goHome() {
    const defaultAppId = services.preferences.getOrgTypePreference('defaultAppId', 'app-switcher');
    if (defaultAppId) {
      const defaultApp = services.app.getAppById(defaultAppId);
      if (defaultApp && defaultApp._links?.self) {
        console.log('PrintOS Header: goHome Navigation - To Default Preference', defaultApp._links?.self);
        services.window.location.href = defaultApp._links?.self;
        return;
      }
    }

    if (!isHPRoaming() && isChannel() && isLFOnly()) {
      const scApp = services.app.getAppById('reseller-portal');
      if (scApp && scApp._links?.self) {
        console.log('PrintOS Header: goHome Navigation - To Service Center', scApp._links?.self);
        services.window.location.href = scApp._links?.self;
        return;
      }
    }

    if (!isHPRoaming() && isPSP() && isLFOnly()) {
      const scApp = services.app.getAppById('live-production');
      if (scApp && scApp._links?.self) {
        console.log('PrintOS Header: goHome Navigation - To Production Hub', scApp._links?.self);
        services.window.location.href = scApp._links?.self;
        return;
      }
    }

    let homeApp = services.app.getAppById('home');
    if (homeApp && homeApp._links?.self) {
      console.log('PrintOS Header: goHome Navigation - To Home', homeApp._links?.self);
      services.window.location.href = homeApp._links?.self;
      return;
    }

    console.log('PrintOS Header: goHome Navigation - To Sign Out (No place to go)');
    goSignOut(services.app.getCurrentAppId() !== 'register');
  }

  function goSignOut(includeTarget) {
    let url;
    let startApp = services.app.getAppById('register');
    if (!startApp?._links?.self) {
      url = '/start/#/signin?signout';
    }
    else {
      url = services.app.getAppById('register')._links.self + '#/signin?signout';
    }

    if (includeTarget) {
      url = appendTarget(url);
    }

    console.log('PrintOS Header: goSignOut Navigation')
    services.window.location.href = url;
  }

  function goSignIn(includeTarget = true) {
    let signInHref = get(sessionStore.cc, '_links.signIn.href');
    if (signInHref) {
      console.log('PrintOS Header: goSignIn Navigation (using CC)', includeTarget ? appendTarget(signInHref) : signInHref);
      services.window.location.href = includeTarget ? appendTarget(signInHref) : signInHref;
    }
    else {
      // If we don't have a sign-in link, create one from the register app.
      // This can happen with a confused, orphaned user that doesn't have a context.
      let registerApp = services.app.getAppById('register');
      if (registerApp) {
        signInHref = get(registerApp, '_links.self');
        if (signInHref) {
          signInHref += '/#/signin';
          let href = includeTarget ? appendTarget(signInHref) : signInHref;
          console.log('PrintOS Header: goSignIn Navigation (using Start app)', href);
          services.window.location.href = href;
        }
      }
      else {
        signInHref = '/start/#/signin';
        console.log('PrintOS Header: goSignIn Navigation (using Hardcoded)', includeTarget ? appendTarget(signInHref) : signInHref);
        services.window.location.href = includeTarget ? appendTarget(signInHref) : signInHref;
      }
    }
  }

  function startContextMonitor() {
    services.window.setInterval(() => {
      if (Object.keys(sessionStore.openContexts).length === 0) {
        return;
      }

      let orgIdsToClose = [];
      Object.keys(sessionStore.openContexts).forEach(orgId => {
        if (sessionStore.openContexts[orgId].closed) {
          orgIdsToClose.push(orgId);
        }
      });

      orgIdsToClose.forEach(orgId => {
        closeContext(orgId);
      });
    }, 1000);
  }

  function startSessionMonitor() {
    services.window.setInterval(() => {
      console.log('PrintOS Header: Checking Session');
      restoreContext();
      redirectIfNotAuthenticated();
    }, MINIMAL_SESSION_TIMEOUT_MILLIS);
  }

  function installUnauthInterceptor() {
    return services.http.responseInterceptor = response => {
      if (response.status === 401) {
        console.log('PrintOS Header: 401 Interceptor');
        redirectIfNotAuthenticated();
      }

      if (response.status === 429) {
        services.ui.addInlineAlert({
          type: 'Http-429-error',
          severity: 'error',
          title: services.i18n.$t('header.inlineAlerts.Http_429_Error'),
          description: services.i18n.$t('header.inlineAlerts.Http_429_Description'),
          actions: [
            {
              type: 'link',
              title: services.i18n.$t('header.inlineAlerts.Http_429_Action'),
              callback: () => {
                services.window.location.reload();
              },
            }
          ],
        });
      }

      return response;
    };
  }

  function redirectIfNotAuthenticated() {
    if (document.hidden) {
      // We are going to pause checking if tab is not active
      // The restoreContext handler should figure out if we have a session when we are activated again
      return;
    }

    return getClientConfigAsync(true).then(resp => {
      sessionStore.setIsAuthenticated(resp._links?.signOut ? true : false);
      if (!isAuthenticated()) {
        console.log('PrintOS Header: SignIn Redirect - Not Authenticated');
        // No longer signed in. Redirect to sign in.
        goSignIn(services.app.getCurrentAppId() !== 'register');
      }

      if (isDevice()) {
        console.log('PrintOS Header: SignIn Redirect - 401 with device');
        goSignIn(true);
      }

      console.log('PrintOS Header: Still Authenticated');
    })
      .catch(e => {
        console.log('PrintOS Header: SignIn Redirect - Failed to get client config', e);
        goSignIn(true);
      });

  }

  function sendClientEvent(eventName, detail = {}) {
    services.window.dispatchEvent(new CustomEvent(eventName, detail ? { detail: detail } : {}));
  }

  function getDebug(category = 'printos_global', level = 0, debug = false) {
    let settings = services.preferences.getLocalStorageObject(`debug-${category}`);
    if (settings) {
      if (Date.now() - settings.savedDate > 24 * 60 * 60 * 1000) {
        services.preferences.deleteLocalStorage(`debug-${category}`);
        settings = null;
      }
    }

    if (!settings) {
      settings = sessionStore.debug[category];
    }

    return settings
  }

  function setDebug(isDebug, category = 'printos_global', level = 0, persist = false) {
    sessionStore.setDebug(isDebug, category, level);
    log({category:'debug'}, `setDebug`, sessionStore.debug[category]);
    if (persist) {
      services.preferences.setLocalStorageObject(`debug-${category}`, {isDebug, level, savedDate: Date.now()});
    }
    else {
      services.preferences.deleteLocalStorage(`debug-${category}`);
    }
  }

  function isDebug(category = 'printos_global', level = 0) {
    let debugSettings = getDebug('debug', 1, false);
    let settings = getDebug(category, level);
    // Only output debug messages if debug settings turned on
    // Note: We can't use 'log' to do this because it would cause a recursive situation
    if (debugSettings?.isDebug) {
      if (settings) {
        let src = settings.savedDate ? 'Persist' : 'Store';
        console.log(`isDebug (${src}): isDebug=${settings.isDebug}, Requested=${category}.${level}`, settings);
      }
      else {
        console.log(`Logging (Unset): isDebug=false, Requested=${category}.${level}`)
      }
    }

    return settings && settings.isDebug && settings.level >= level;
  }

  function log(settings, ...args) {
    let category = settings?.category ? settings.category : 'printos_global';
    let level = settings?.level ? settings.level : 0;
    if (isDebug(category, level)) {
      console.log.apply(this, args);
    }
  }

  function appendTarget(href) {
    if (href.includes('target=')) {
      return;
    }

    if (href.indexOf('?') >= 0) {
      href += '&';
    } else {
      href += '?';
    }
    return `${href}target=${services.window.encodeURIComponent(sessionStore.targetUrl ? sessionStore.targetUrl : services.window.location.href)}`;
  }

  function hasValue(test) {
    return !(test === undefined || test === null);
  }
}
